import { StorePersistService } from '../checkout/modules/checkout-shared/services/store-persist.service';
import type { UploadPrintfileInput } from '../file-api/file-api.service';
import type {
	FileItem as FileItemLegacy,
	UploadComponent as UploadLegacyComponent,
} from './upload-legacy/upload.component';
import type { FileItem as FileItemV2, UploadComponent } from './upload-v2/upload.component';
import { NgComponentOutlet } from '@angular/common';
import {
	Component,
	ComponentRef,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewContainerRef,
	output,
} from '@angular/core';

export type FileItem = FileItemV2 | FileItemLegacy;
/** A component that renders the new or legacy upload component based on the feature flag. Uses ngComponentOutlet to render the component.
 * It:
 *  - Renders either the new or legacy upload component based on the "enableNewFileInfra" flag that is read from the StorePersistService.
 *  - Passes through inputs and outputs on to and from the upload component.
 * @todo After the legacy upload component is not needed anymore, remove this component too and just the new one directly.
 */
@Component({
	standalone: true,
	imports: [NgComponentOutlet],
	selector: 'g-upload',
	template: `<ng-template #container></ng-template>`,
})
export class DynamicUploadComponent implements OnInit, OnDestroy {
	@Input() multiple = false;
	@Input() options!: UploadPrintfileInput | null;
	@Input() fileType?: string;
	readonly onUploadComplete = output<FileItem>();
	readonly onUploadStart = output<FileItem>();
	readonly onClear = output<FileItem>();
	readonly onFiletypeError = output<File[]>();
	isNewFileInfraEnabled = this.persist.getFlag('enableNewFileInfra');
	@ViewChild('container', { read: ViewContainerRef, static: true }) container!: ViewContainerRef;

	passthroughProps = [
		'multiple',
		'options',
		'onUploadComplete',
		'onUploadStart',
		'onClear',
		'onFiletypeError',
	] as (keyof DynamicUploadComponent)[];

	// Use dynamic imports to load the new / legacy upload component depending whether the feature flag is set or not.
	readonly componentRef: Promise<ComponentRef<UploadComponent | UploadLegacyComponent>> = this
		.isNewFileInfraEnabled
		? import('./upload-v2/upload.component').then(({ UploadComponent }) => {
				const component = this.container.createComponent(UploadComponent);
				this.passthroughProps.push('fileType');
				return component;
			})
		: import('./upload-legacy/upload.component').then(({ UploadComponent }) => {
				return this.container.createComponent(UploadComponent);
			});

	constructor(private readonly persist: StorePersistService) {}

	ngOnInit(): void {
		void this.componentRef.then(({ instance }) => {
			const passthroughProps = this.passthroughProps.map((propName) => [propName, this[propName]]);
			for (const [key, prop] of passthroughProps as [keyof typeof instance, unknown][]) {
				if (prop !== undefined) {
					(instance[key] as unknown) = prop;
				}
			}
		});
	}

	ngOnDestroy(): void {
		void this.componentRef?.then((ref) => ref.destroy());
	}

	@Input()
	set uploadedFiles(files: FileItem | FileItem[] | undefined) {
		void this.componentRef.then(({ instance }) => {
			instance.uploadedFiles = files;
		});
	}
	@Input() set acceptedFiletypes(fileTypes: string) {
		void this.componentRef.then(({ instance }) => {
			instance.acceptedFiletypes = fileTypes;
		});
	}
}
